/**
 * How an invoice status is drawn and named, in one place.
 *
 * Three screens show one — the billing list, the tracker table and the invoice
 * form — and two of them already carried their own copy of the tone map, which
 * had begun to disagree (`text-amber-800` against `text-amber-700`). One
 * vocabulary, so a status that means the same thing looks the same wherever
 * somebody meets it.
 *
 * On the override
 * ---------------
 * `Invoice.status` is derived from the payments and stays that way. An
 * administrator can record a *different* answer beside it — a client who
 * settled by cheque offline is the case it exists for — and the API sends both
 * the effective status and the derived one so a screen can show that a
 * decision was made rather than only its result.
 *
 * Which is the point of `overriddenTone`. An overridden figure that looks
 * identical to a computed one is the version of this feature that does harm:
 * the number moves and nothing says why.
 */
import type { InvoiceStatus } from '../types';

export const STATUS_TONE: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  raised: 'bg-blue-100 text-blue-700',
  partially_paid: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
  written_off: 'bg-gray-200 text-gray-500',
};

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'draft',
  raised: 'raised',
  partially_paid: 'part paid',
  paid: 'paid',
  overdue: 'overdue',
  cancelled: 'cancelled',
  written_off: 'written off',
};

/**
 * What an override may say.
 *
 * Only the two that are about the money. Draft and raised are positions in the
 * document's workflow, reached by raising it or not; cancelled is already a
 * decision with its own route and its own consequences. The server refuses
 * anything else, and this is the same list so the dropdown cannot offer a
 * choice the API will reject.
 */
export const OVERRIDABLE: InvoiceStatus[] = ['paid', 'partially_paid'];

/** A dotted ring, so an overridden badge is visibly not a computed one. */
export const OVERRIDDEN_RING = 'ring-1 ring-dashed ring-gray-400';

export function statusLabel(status: InvoiceStatus): string {
  return STATUS_LABELS[status] ?? status;
}
